import { useMemo } from 'react';
import { cn } from '@/utils/utils.ts';
import { Separator } from '@/components/ui/separator.tsx';
import {
  getUptimeStatusConfig,
  TUptimeStatusKeys,
  UptimeStatusOptions,
} from '@/components/uptime/uptime-status-config';
import { UptimeStatusBar } from '@/components/uptime/uptime-status-bar.tsx';
import { GetPlantUptimeStatusResponse } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-uptime-status.ts';

const HOURS_IN_DAY = 24;

const getAlignment = (index: number) => {
  if (index < 6) return 'start';
  if (index >= 6 && index < 12) return 'center';
  return 'end';
};

export const UptimeStatusTimeline = ({
  data,
  currentStatus,
}: {
  data?: GetPlantUptimeStatusResponse;
  currentStatus?: TUptimeStatusKeys;
}) => {
  const hourlyStatusRecords = useMemo(() => {
    const availableHoursCount = data?.length ?? 0;
    const missingHoursCount = HOURS_IN_DAY - availableHoursCount;
    let completeHourlyRecords = [];

    while (completeHourlyRecords.length < missingHoursCount) {
      completeHourlyRecords.push({
        [UptimeStatusOptions.NoData]: 100,
        [UptimeStatusOptions.Disconnected]: 0,
        [UptimeStatusOptions.Connected]: 0,
        time: '',
      });
    }
    if (data) {
      completeHourlyRecords = [...completeHourlyRecords, ...data];
    }

    return completeHourlyRecords;
  }, [data]);

  const statusSummary = useMemo(() => {
    return hourlyStatusRecords?.reduce(
      (acc, curr) => {
        return {
          uptime: acc.uptime + curr[UptimeStatusOptions.Connected],
          downtime: acc.downtime + curr[UptimeStatusOptions.Disconnected],
          noData: acc.noData + curr[UptimeStatusOptions.NoData],
        };
      },
      {
        uptime: 0,
        downtime: 0,
        noData: 0,
      }
    );
  }, [hourlyStatusRecords]);

  const serviceAvailabilityPercentage = useMemo(() => {
    if (!statusSummary) return 0;

    const totalMonitoredTime = statusSummary.uptime + statusSummary.downtime;
    return totalMonitoredTime > 0
      ? Math.trunc((statusSummary.uptime * 100) / totalMonitoredTime)
      : 0;
  }, [statusSummary]);

  const statusDisplayConfig = getUptimeStatusConfig(currentStatus ?? -1);

  return (
    <div className="flex w-full flex-col gap-4 bg-white px-3">
      <div className={cn('relative hidden flex-col gap-2', `min-[900px]:flex`)}>
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className={cn(`size-3 rounded-full`, statusDisplayConfig.backgroundColor)} />
            <span className="text-sm font-medium">{statusDisplayConfig.label}</span>
          </div>
          <div className="text-muted-foreground text-xs font-medium">
            {serviceAvailabilityPercentage}% uptime
          </div>
        </div>
        <div className="flex w-full gap-2 pb-6">
          {hourlyStatusRecords?.map((record, index) => (
            <UptimeStatusBar
              key={`desktop-${index}`}
              data={record}
              showTime={[0, 4, 8, 12, 16, 20, 23].includes(index)}
              align={getAlignment(index)}
            />
          ))}
        </div>
      </div>

      <div className={cn('flex flex-col gap-3', `min-[900px]:hidden`)}>
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div
              className={cn(`mt-0.5 size-3 rounded-full`, statusDisplayConfig.backgroundColor)}
            />
            <span className="text-sm font-medium">{statusDisplayConfig.label}</span>
          </div>
          <div className="text-muted-foreground text-xs font-medium">
            {serviceAvailabilityPercentage}% uptime
          </div>
        </div>
        <div className="relative pb-6">
          <div className="flex w-full gap-2">
            {hourlyStatusRecords
              ?.slice(0, 12)
              .map((record, index) => (
                <UptimeStatusBar
                  key={`mobile-first-${index}`}
                  data={record}
                  showTime={[0, 4, 8, 11].includes(index)}
                  align={index + 1 < 6 ? 'start' : 'end'}
                />
              ))}
          </div>
        </div>
        <Separator />
        <div className="relative pb-6">
          <div className="flex w-full gap-2">
            {hourlyStatusRecords
              ?.slice(12, 24)
              .map((record, index) => (
                <UptimeStatusBar
                  key={`mobile-second-${index}`}
                  data={record}
                  showTime={[0, 4, 8, 11].includes(index)}
                  align={index + 1 < 6 ? 'start' : 'end'}
                />
              ))}
          </div>
        </div>
      </div>

      <StatusBarsLegend />
    </div>
  );
};

const StatusBarsLegend = () => {
  const connectedConfig = getUptimeStatusConfig(UptimeStatusOptions.Connected);
  const disconnectedConfig = getUptimeStatusConfig(UptimeStatusOptions.Disconnected);
  const noDataConfig = getUptimeStatusConfig(UptimeStatusOptions.NoData);

  return (
    <div
      className={cn('flex w-[230px] flex-wrap justify-between gap-2', `min-[900px]:w-full`)}
    >
      <div className="flex items-center gap-2" role="listitem" aria-label="Connected status">
        <div className={cn('size-3 rounded-full', connectedConfig.backgroundColor)}></div>
        <span className="text-muted-foreground text-sm">{connectedConfig.label}</span>
      </div>
      <div className="flex items-center gap-2" role="listitem">
        <div className={cn('size-3 rounded-full', disconnectedConfig.backgroundColor)}></div>
        <span className="text-muted-foreground text-sm">{disconnectedConfig.label}</span>
      </div>
      <div className="flex items-center gap-2" role="listitem" aria-label="No Data status">
        <div className={cn('size-3 rounded-full', noDataConfig.backgroundColor)}></div>
        <span className="text-muted-foreground text-sm">{noDataConfig.label}</span>
      </div>
    </div>
  );
};
